// Shortlist timeframe: what happened, by the timeframe the buyer chose.
//
// The start-choice surface asks a Mira-lane client how long Mira should collect
// proposals before showing the shortlist (2h / 4h / 12h, Monday 3202193284).
// This page is that choice's readout (Monday 3204302464): who reached the picker
// and what they did there, then one row per timeframe with how many chose it,
// what the window collected, and what the buyer did once the shortlist was
// revealed. One aggregate payload (`GET /api/admin/shortlist-timeframe`), rolled
// up by the route over the window picked here, so the rows can never disagree.
//
// Two things about the design are deliberate, both borrowed from the cohorts
// page beside it:
//
// - **Every rate says what it rests on.** The count sits beside each figure and
//   a thin one is tinted, because a 100% on two runs and a 100% on two hundred
//   are different facts.
// - **An unmeasured cell reads "-", never 0.** A timeframe whose reveals are all
//   inside the three-day holdback has nothing to say about picks yet; printing
//   0% there would turn "not judged yet" into "nobody picked".
import { useQuery } from "@tanstack/react-query";
import { TableExport } from "@recruiter/shared";
import type { TimeframeRate, TimeframeRow, TimeframeWindow } from "@recruiter/shared/types";

import { AreaBoundary } from "../components/area-gate";
import { CohortTabs } from "../components/cohort-tabs";
import { EmptyState, LoadingState } from "../components/states";
import { formatClock, formatDateTime } from "../lib/format";
import { shortlistTimeframeQuery } from "../lib/queries";
import {
  formatMean,
  formatRate,
  hoursLabel,
  isThin,
  pickerSegments,
  restsOn,
  TIMEFRAME_WINDOW_LABELS,
  TIMEFRAME_WINDOWS,
} from "../lib/shortlist-timeframe";
import "./shortlist-timeframe.css";

function RateCell({ rate }: { rate: TimeframeRate }) {
  return (
    <td className={`stf-table__num${isThin(rate.count) ? " stf-table__num--thin" : ""}`}>
      <span className="stf-table__figure">{formatRate(rate.rate)}</span>
      {rate.count > 0 ? <span className="stf-table__of">{restsOn(rate.count)}</span> : null}
    </td>
  );
}

function TimeframeRowView({ row }: { row: TimeframeRow }) {
  return (
    <tr>
      <th scope="row" className="stf-table__name">
        {hoursLabel(row.hours)}
      </th>
      <td className="stf-table__num">
        <span className="stf-table__figure">{row.runs}</span>
        <span className="stf-table__of">{formatRate(row.share)}</span>
      </td>
      <td className={`stf-table__num${isThin(row.proposals.count) ? " stf-table__num--thin" : ""}`}>
        <span className="stf-table__figure">{formatMean(row.proposals.mean)}</span>
        {row.proposals.count > 0 ? (
          <span className="stf-table__of">{restsOn(row.proposals.count)}</span>
        ) : null}
      </td>
      <RateCell rate={row.revealed} />
      <RateCell rate={row.noResults} />
      <RateCell rate={row.extended} />
      <RateCell rate={row.returned} />
      <RateCell rate={row.showMore} />
      <RateCell rate={row.picked} />
      <RateCell rate={row.pickedTop} />
      <RateCell rate={row.pickedExtra} />
    </tr>
  );
}

export function ShortlistTimeframePage({
  window,
  onWindowChange,
}: {
  window: TimeframeWindow;
  onWindowChange: (next: TimeframeWindow) => void;
}) {
  const query = useQuery(shortlistTimeframeQuery(window));
  const data = query.data ?? null;
  const segments = data ? pickerSegments(data.picker) : [];
  const topLabel = data && data.topN > 0 ? `top ${data.topN}` : "top cards";

  return (
    <div className="ad-page">
      <CohortTabs active="timeframe" />
      <header className="ad-page__head">
        <h1 className="ad-page__title">Shortlist timeframe</h1>
        <p className="ad-page__sub">
          A Mira-lane client chooses how long Mira collects proposals before the shortlist is shown
          (2, 4 or 12 hours). This is what happened by that choice: who reached the picker and what
          they did there, then each timeframe's runs, the proposals its window collected, and what
          the buyer did once the shortlist was revealed. Read the count beside every figure before
          reading much into it.
        </p>
      </header>

      <div className="stf-controls">
        <div className="stf-windows" role="group" aria-label="Roll-up window">
          {TIMEFRAME_WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              className={`stf-win${w === window ? " stf-win--active" : ""}`}
              aria-pressed={w === window}
              onClick={() => onWindowChange(w)}
            >
              {TIMEFRAME_WINDOW_LABELS[w]}
            </button>
          ))}
        </div>
        {/* Measured by the worker every fifteen minutes, not on this request
            (backend/recruiter/shortlist_timeframe_snapshot.py). Say when. */}
        {data ? (
          <span className="stf-stamp" title={formatDateTime(data.generatedAt)}>
            Updated {formatClock(data.generatedAt)}
          </span>
        ) : null}
      </div>

      {query.isPending ? <LoadingState label="Rolling up the timeframes…" /> : null}
      {query.isError ? (
        <AreaBoundary
          area="version_cohorts"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {data ? (
        <div className={`stf-body${query.isPlaceholderData ? " stf-body--dim" : ""}`}>
          <section className="stf-picker" aria-labelledby="stf-picker-title">
            <h2 id="stf-picker-title" className="stf-picker__title">
              At the picker
            </h2>
            <p className="stf-picker__lede">
              <strong>{data.picker.shown}</strong> projects were shown the timeframe tiles and had a
              day to answer.
            </p>
            {data.picker.shown > 0 ? (
              <>
                <div
                  className="stf-picker__bar"
                  role="img"
                  aria-label={segments
                    .map((s) => `${s.label}: ${s.count} (${formatRate(s.share)})`)
                    .join(", ")}
                >
                  {segments.map((s) =>
                    s.share !== null && s.share > 0 ? (
                      <span
                        key={s.key}
                        className={`stf-picker__seg stf-picker__seg--${s.key}`}
                        style={{ flexGrow: s.share }}
                      />
                    ) : null,
                  )}
                </div>
                <ul className="stf-picker__legend">
                  {segments.map((s) => (
                    <li key={s.key} className="stf-picker__item">
                      <span className={`stf-picker__swatch stf-picker__swatch--${s.key}`} />
                      <span className="stf-picker__label">{s.label}</span>
                      <span className="stf-picker__count">
                        {s.count} <span className="stf-picker__share">{formatRate(s.share)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="stf-picker__empty">
                Nobody reached the picker in this window yet, or the newest day is still inside its
                holdback.
              </p>
            )}
          </section>

          {data.rows.length === 0 ? (
            <EmptyState
              title="No timeframe chosen in this window yet"
              message="A row appears the first time a client picks a timeframe and starts Mira. Try a longer window."
            />
          ) : (
            <div className="stf-table-wrap">
              <table className="stf-table">
                <TableExport name="shortlist-timeframes" label="timeframes" />
                <thead>
                  <tr>
                    <th scope="col" className="stf-table__head stf-table__head--name">
                      Timeframe
                    </th>
                    <th
                      scope="col"
                      className="stf-table__head"
                      title="Runs that chose it, and their share of every timeframe's runs."
                    >
                      Chosen
                    </th>
                    <th
                      scope="col"
                      className="stf-table__head"
                      title="Proposals received per run, over runs whose window has closed."
                    >
                      Proposals
                    </th>
                    <th
                      scope="col"
                      className="stf-table__head"
                      title="Of closed windows, the share that revealed a shortlist."
                    >
                      Shortlist shown
                    </th>
                    <th
                      scope="col"
                      className="stf-table__head"
                      title="Of closed windows, the share that ended with no results and sent the client to search."
                    >
                      Closed empty
                    </th>
                    <th
                      scope="col"
                      className="stf-table__head"
                      title="Of closed windows, the share that closed empty and got the one extra hour."
                    >
                      Extra hour
                    </th>
                    <th
                      scope="col"
                      className="stf-table__head"
                      title="Of shortlists revealed 3+ days ago, the share whose client came back to see it within 3 days."
                    >
                      Came back
                    </th>
                    <th
                      scope="col"
                      className="stf-table__head"
                      title="Of shortlists revealed 3+ days ago, the share whose client pressed Show more within 3 days."
                    >
                      Show more
                    </th>
                    <th
                      scope="col"
                      className="stf-table__head"
                      title="Of shortlists revealed 3+ days ago, the share whose client picked someone within 3 days."
                    >
                      Picked
                    </th>
                    <th
                      scope="col"
                      className="stf-table__head"
                      title={`Of shortlists revealed 3+ days ago, the share whose client picked one of the ${topLabel} within 3 days.`}
                    >
                      From the {topLabel}
                    </th>
                    <th
                      scope="col"
                      className="stf-table__head"
                      title="Of shortlists revealed 3+ days ago, the share whose client picked from the proposals behind Show more within 3 days."
                    >
                      From the extras
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <TimeframeRowView key={row.hours} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* The methodological caveats a reader has to hold to use this page
              honestly, on screen rather than in a doc. */}
          <p className="stf-footnote">
            Rates after the reveal count what the buyer did within three days and hold the newest
            three days back until each shortlist's window is up; the picker holds back a day the
            same way. "From the {topLabel}" and "From the extras" can both be true for one project
            who picked twice. A timeframe nobody chose in this window is not listed.
          </p>
        </div>
      ) : null}
    </div>
  );
}
